import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import { QrCode, Gift, Sparkles, Info, CheckCircle2, LogOut, RotateCcw, ShieldCheck, Copy, Check, ChevronDown, UserRound } from "lucide-react";
import { StampCard } from "./StampCard";
import appleWalletBadge from "@/assets/apple-wallet/DE_Add_to_Apple_Wallet_RGB_101421.svg";
import googleWalletBadge from "@/assets/google-wallet/de_add_to_google_wallet_add-wallet-badge.svg";
import { BonsteloIcon } from "./Layout";
import { useAuth } from "@/AuthContext";
import { createLoyaltyAction, getMyLoyaltyAction, addGoogleWallet, addAppleWallet } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

export const CustomerView = ({ state, onRefresh }) => {
  const { logout, restoreCustomer } = useAuth();
  const navigate = useNavigate();
  const { business, customer, progress, rewards } = state;
  const isPoints = progress.mode === "points_spend";
  const unit = isPoints ? "Punkte" : "Stempel";

  const [qrOpen, setQrOpen] = useState(false);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreCode, setRestoreCode] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedCopied, setSavedCopied] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const appleWalletOn = !!(business.wallet?.enabled && business.wallet?.apple_enabled);
  const openAppleWallet = async () => {
    if (appleBusy) return;
    setAppleBusy(true);
    try {
      const data = await addAppleWallet();
      const blob = new Blob([data], { type: "application/vnd.apple.pkpass" });
      const url = URL.createObjectURL(blob);
      window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Apple Wallet ist derzeit nicht verfügbar.");
    } finally { setAppleBusy(false); }
  };

  const googleWalletOn = !!(business.wallet?.enabled && business.wallet?.google_enabled);
  const openGoogleWallet = async () => {
    if (walletBusy) return;
    setWalletBusy(true);
    try {
      const r = await addGoogleWallet();
      if (r?.url) window.location.assign(r.url);
      else toast.error("Google Wallet ist derzeit nicht verfügbar.");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Google Wallet ist derzeit nicht verfügbar.");
    } finally { setWalletBusy(false); }
  };

  const remaining = Math.max(progress.target - progress.current, 0);
  const availableRewards = rewards.filter((r) => r.status === "available");
  const redeemedRewards = rewards.filter((r) => r.status === "redeemed");
  const savedCode = (() => { try { return localStorage.getItem(`bs_recovery:${business.slug}`); } catch (e) { return null; } })();
  const copySaved = async () => { try { await navigator.clipboard.writeText(savedCode); setSavedCopied(true); setTimeout(() => setSavedCopied(false), 1500); } catch (e) {} };

  // Phase 1: customer-initiated loyalty action (points_spend / stamp_fixed only).
  const supportsAction = isPoints || progress.mode === "stamp_fixed";
  const actionLabel = isPoints ? "Punkte sammeln" : "Stempel sammeln";
  const requestsBlocked = !!customer.requests_blocked;  // Phase 2: request feature disabled by owner
  const canRequest = supportsAction && !requestsBlocked;
  const [action, setAction] = useState(null);
  const [remainingSec, setRemainingSec] = useState(0);

  const startAction = async () => {
    try {
      const a = await createLoyaltyAction();
      setAction(a);
    } catch (e) { /* still show the QR so staff can scan the existing code */ }
    setQrOpen(true);
  };

  useEffect(() => {
    if (!qrOpen || !action || action.status !== "pending") return;
    let alive = true;
    const tick = () => setRemainingSec(Math.max(0, Math.round((new Date(action.expires_at).getTime() - Date.now()) / 1000)));
    tick();
    const tIv = setInterval(tick, 1000);
    const pIv = setInterval(async () => {
      try {
        const a = await getMyLoyaltyAction(action.id);
        if (!alive) return;
        if (a.status === "confirmed") { setAction(null); setQrOpen(false); toast.success("Gebucht ✓"); onRefresh && onRefresh(); }
        else if (a.status === "expired") { setAction(null); setQrOpen(false); toast.info("Die Anfrage ist abgelaufen."); }
        else setAction((prev) => (prev ? { ...prev, ...a } : a));
      } catch (e) { /* transient */ }
    }, 2500);
    return () => { alive = false; clearInterval(tIv); clearInterval(pIv); };
  }, [qrOpen, action?.id, action?.status, onRefresh]);

  const prevAvailable = useRef(availableRewards.length);
  useEffect(() => {
    if (availableRewards.length > prevAvailable.current) {
      const colors = [business.primary_color, business.secondary_color, "#ffffff"];
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.4 }, colors });
      setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { y: 0.5 }, colors }), 250);
      setCelebrate(true);
    }
    prevAvailable.current = availableRewards.length;
  }, [availableRewards.length, business.primary_color, business.secondary_color]);

  if (!business.active) return (
    <div data-testid="customer-unavailable" className="p-6 pt-10 text-center">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3"><Info className="h-7 w-7 text-slate-400" /></div>
      <p className="font-display font-extrabold text-xl text-slate-900">Programm derzeit nicht verfügbar</p>
      <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">Das Treueprogramm von {business.name} ist momentan pausiert. Deine Stempel bleiben gespeichert.</p>
    </div>
  );

  const handleRestore = async () => {
    if (restoreBusy) return;
    setRestoreBusy(true);
    try {
      await restoreCustomer(restoreCode.trim());
      try { localStorage.setItem(`bs_recovery:${business.slug}`, restoreCode.trim()); } catch (e) {}
      setRestoreOpen(false); setRestoreCode("");
      toast.success("Treuekarte wiederhergestellt ✓");
      window.location.reload();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Wiederherstellungscode nicht gefunden oder ungültig.");
    } finally { setRestoreBusy(false); }
  };

  const handleLogout = async () => {
    await logout(business.id);
    navigate(`/join/${business.slug}?loggedout=1`);
  };

  return (
    <div className="px-4 pt-3 space-y-3 pb-8">
      <div data-testid="customer-summary" className="animate-float-up flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-slate-400 text-[12px] font-semibold truncate">Hallo {customer.name} 👋</p>
          <h1 data-testid="customer-header-title" className="font-display font-extrabold text-[20px] leading-tight text-slate-900 tracking-tight">Deine Treuekarte</h1>
        </div>
        <div className="text-right shrink-0">
          <p data-testid="stamp-count-text" className="font-display font-extrabold text-[26px] leading-none" style={{ color: "var(--brand-primary)" }}>
            {progress.current}{!isPoints && <span className="text-slate-300 text-lg">/{progress.target}</span>}
          </p>
          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">{unit}</p>
        </div>
      </div>

      {canRequest ? (
        <Button data-testid="collect-action-button" onClick={startAction}
          className="w-full h-12 rounded-2xl text-base font-display font-bold shadow-brand press text-white" style={{ background: "var(--brand-primary)" }}>
          <QrCode className="mr-2 h-5 w-5" /> {actionLabel}
        </Button>
      ) : (
        <Button data-testid="get-stamp-button" onClick={() => setQrOpen(true)}
          className="w-full h-12 rounded-2xl text-base font-display font-bold shadow-brand press text-white" style={{ background: "var(--brand-primary)" }}>
          <QrCode className="mr-2 h-5 w-5" /> Meinen Code zeigen
        </Button>
      )}
      {supportsAction && requestsBlocked && (
        <p data-testid="customer-requests-blocked-note" className="text-center text-xs text-slate-400 font-medium">Bitte zeige deine Treuekarte direkt beim Personal vor.</p>
      )}

      {isPoints ? (
        <div data-testid="points-card-container" className="bg-white rounded-[22px] ring-1 ring-slate-100 shadow-brand overflow-hidden">
          <div className="brand-wave card-sheen px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-white/85 text-[10px] font-bold uppercase tracking-[0.18em]">Treuekarte</p>
              <p className="text-white font-display font-extrabold text-[15px] leading-tight truncate">{business.name}</p>
            </div>
            <div className="bg-white/20 rounded-full px-3 py-1 backdrop-blur shrink-0">
              <span data-testid="points-count-badge" className="text-white font-display font-extrabold text-sm">{progress.current}/{progress.target}</span>
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <motion.div className="h-full rounded-full brand-wave" initial={{ width: 0 }} animate={{ width: `${progress.percent}%` }} transition={{ duration: 0.7, ease: "easeOut" }} />
            </div>
            <div className="flex justify-between mt-1.5 text-[11px] font-semibold text-slate-400">
              <span>{progress.current} Punkte</span><span>{progress.target} Punkte</span>
            </div>
          </div>
        </div>
      ) : (
        <StampCard stamps={progress.current} total={progress.target} businessName={business.name} logoUrl={business.logo_url} />
      )}

      <div data-testid="reward-summary" className="bg-white rounded-2xl ring-1 ring-slate-100 px-4 py-3 shadow-soft flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "color-mix(in srgb, var(--brand-secondary) 14%, white)" }}>
          <Gift className="h-[18px] w-[18px]" style={{ color: "var(--brand-secondary)" }} />
        </div>
        <div className="min-w-0">
          <p data-testid="reward-summary-title" className="font-display font-extrabold text-[15px] text-slate-900 leading-tight truncate">{business.reward_title}</p>
          <p data-testid="remaining-stamps-text" className="text-[12.5px] text-slate-500 font-medium mt-0.5">
            {availableRewards.length > 0 || remaining === 0
              ? <span className="font-bold text-emerald-600">Belohnung verfügbar</span>
              : <>Noch <span className="font-bold" style={{ color: "var(--brand-primary)" }}>{remaining} {unit}</span> bis zur Belohnung</>}
          </p>
        </div>
      </div>

      {(availableRewards.length > 0 || redeemedRewards.length > 0) && (
      <div data-testid="rewards-section" className="space-y-2.5 pt-1">
        <h2 className="font-display font-extrabold text-base text-slate-900">Deine Belohnungen</h2>
        {availableRewards.map((r) => (
          <motion.div key={r.id} data-testid="reward-card-free-doner" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className="relative overflow-hidden rounded-2xl p-4 ring-1 ring-slate-100 bg-white shadow-soft flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl brand-wave flex items-center justify-center shadow-sm shrink-0"><Gift className="h-6 w-6 text-white" /></div>
              <div>
                <p className="font-display font-extrabold text-[15px] text-slate-900 leading-tight">{r.title}</p>
                <span className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-bold text-emerald-600 bg-emerald-600/10 rounded-full px-2 py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 animate-pulse-dot" /> Verfügbar
                </span>
              </div>
            </div>
            <Button data-testid="show-reward-button" onClick={() => setRewardOpen(true)} className="rounded-xl bg-white ring-1 hover:bg-slate-50 font-bold shadow-sm press shrink-0"
              style={{ color: "var(--brand-primary)", borderColor: "var(--brand-secondary)" }}>Anzeigen</Button>
          </motion.div>
        ))}
        {redeemedRewards.map((r) => (
          <div key={r.id} className="rounded-2xl p-3.5 border border-slate-100 bg-slate-50/60 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-slate-200/70 flex items-center justify-center shrink-0"><CheckCircle2 className="h-5 w-5 text-slate-400" /></div>
            <div><p className="font-semibold text-slate-500 line-through text-sm">{r.title}</p><p className="text-[11px] text-slate-400 font-medium">Eingelöst</p></div>
          </div>
        ))}
      </div>
      )}

      {(googleWalletOn || appleWalletOn) && (
        <div data-testid="wallet-badges" className="flex flex-wrap justify-center items-center gap-x-[14px] gap-y-2 pt-1">
          {googleWalletOn && (
            <button type="button" data-testid="google-wallet-button" onClick={openGoogleWallet} disabled={walletBusy}
              aria-label="Zu Google Wallet hinzufügen"
              className="p-0 bg-transparent border-0 transition-opacity duration-200 disabled:opacity-50 active:opacity-80">
              <img src={googleWalletBadge} alt="Zu Google Wallet hinzufügen" className="block h-12 w-auto max-w-none" draggable="false" />
            </button>
          )}
          {appleWalletOn && (
            <button type="button" data-testid="apple-wallet-button" onClick={openAppleWallet} disabled={appleBusy}
              aria-label="Zu Apple Wallet hinzufügen"
              className="p-0 bg-transparent border-0 transition-opacity duration-200 disabled:opacity-50 active:opacity-80">
              <img src={appleWalletBadge} alt="Zu Apple Wallet hinzufügen" className="block h-12 w-auto max-w-none" draggable="false" />
            </button>
          )}
        </div>
      )}

      <div data-testid="account-section" className="pt-1">
        <div className="rounded-2xl bg-white ring-1 ring-slate-100 shadow-soft divide-y divide-slate-100 overflow-hidden">
          <button type="button" data-testid="account-toggle-button" onClick={() => setAccountOpen((o) => !o)} aria-expanded={accountOpen}
            className="w-full flex items-center gap-3 px-4 py-3 text-left press">
            <UserRound className="h-4 w-4 text-slate-400" />
            <span className="flex-1 text-sm font-semibold text-slate-700">Mein Konto</span>
            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${accountOpen ? "rotate-180" : ""}`} />
          </button>
          {accountOpen && (<>
          <button data-testid="save-card-button" onClick={() => setSavedOpen(true)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left press">
            <ShieldCheck className="h-4 w-4 text-slate-400" /><span className="text-sm font-semibold text-slate-700">Karte sichern</span>
          </button>
          <button data-testid="restore-open-button" onClick={() => setRestoreOpen(true)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left press">
            <RotateCcw className="h-4 w-4 text-slate-400" /><span className="text-sm font-semibold text-slate-700">Treuekarte wiederherstellen</span>
          </button>
          <button data-testid="customer-logout-button" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3.5 text-left press">
            <LogOut className="h-4 w-4 text-slate-400" /><span className="text-sm font-semibold text-slate-500">Abmelden</span>
          </button>
          </>)}
        </div>
        <div className="pt-4 pb-2 flex items-center justify-center gap-1.5 opacity-70">
          <BonsteloIcon className="h-4 w-4" />
          <span className="text-[11px] font-semibold text-slate-400">powered by Bonstelo</span>
        </div>
      </div>

      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="rounded-3xl max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-display text-center">Treuekarte wiederherstellen</DialogTitle>
            <DialogDescription className="text-center leading-relaxed">Gib deinen Wiederherstellungscode ein, um deine bestehende Karte auf diesem Gerät zu öffnen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <label className="text-xs font-bold text-slate-500">Wiederherstellungscode</label>
            <Input data-testid="account-restore-code-input" value={restoreCode} onChange={(e) => setRestoreCode(e.target.value)} placeholder="z. B. A7K4-P9Q2" className="h-12 rounded-xl text-center uppercase tracking-widest font-display font-bold" />
            <Button data-testid="account-restore-submit-button" onClick={handleRestore} disabled={restoreBusy || !restoreCode.trim()}
              className="w-full h-12 rounded-2xl font-display font-bold press disabled:opacity-60 text-white" style={{ background: "var(--brand-primary)" }}>
              {restoreBusy ? "Bitte warten…" : "Wiederherstellen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={savedOpen} onOpenChange={setSavedOpen}>
        <DialogContent data-testid="save-card-modal" className="rounded-3xl max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-display text-center">Karte sichern</DialogTitle>
            <DialogDescription className="text-center leading-relaxed">Mit diesem Code kannst du deine Treuekarte auf einem anderen Gerät wiederherstellen.</DialogDescription>
          </DialogHeader>
          {savedCode ? (
            <div className="space-y-3 py-1">
              <div className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3" style={{ background: "color-mix(in srgb, var(--brand-primary) 8%, white)" }}>
                <span data-testid="saved-recovery-code" className="font-display font-extrabold text-2xl tracking-[0.15em]" style={{ color: "var(--brand-primary)" }}>{savedCode}</span>
                <button data-testid="copy-saved-code" onClick={copySaved} className="text-slate-500 press" aria-label="Code kopieren">{savedCopied ? <Check className="h-5 w-5 text-emerald-600" /> : <Copy className="h-5 w-5" />}</button>
              </div>
              <p className="text-xs text-slate-400 text-center">Bewahre den Code sicher auf.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-2">Dein Recovery-Code ist auf diesem Gerät nicht verfügbar.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={qrOpen} onOpenChange={(o) => { setQrOpen(o); if (!o) setAction(null); }}>
        <DialogContent data-testid="customer-qr-modal" className="rounded-3xl max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-display text-center">Zeig diesen Code dem Mitarbeiter</DialogTitle>
            <DialogDescription className="text-center">Der Mitarbeiter scannt diesen Code, um deine Karte zu buchen.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="p-4 bg-white rounded-2xl border-2" style={{ borderColor: "color-mix(in srgb, var(--brand-primary) 20%, white)" }}>
              <QRCodeSVG data-testid="customer-qr-code" value={customer.id} size={240} fgColor="#000000" bgColor="#FFFFFF" level="M" />
            </div>
            <p className="font-display font-extrabold text-slate-900">{customer.name}</p>
            <p className="text-sm font-semibold" style={{ color: "var(--brand-primary)" }}>Zeige diesen QR-Code an der Kasse.</p>
            {action && (
              <div className="text-center" data-testid="customer-action-status">
                <p className="text-sm font-bold" style={{ color: "var(--brand-primary)" }}>Anfrage aktiv</p>
                {remainingSec > 0 && <p className="text-xs text-slate-400">Läuft ab in {remainingSec}s</p>}
              </div>
            )}
            <p className="text-xs text-slate-500 flex items-center gap-1"><Info className="h-3 w-3" /> {business.reward_title}</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rewardOpen} onOpenChange={setRewardOpen}>
        <DialogContent className="rounded-3xl max-w-xs text-center">
          <DialogHeader>
            <DialogTitle className="font-display text-center">Deine Belohnung</DialogTitle>
            <DialogDescription className="text-center">Deine gesammelte Belohnung im Überblick.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="h-20 w-20 rounded-3xl brand-wave flex items-center justify-center shadow-brand"><Gift className="h-10 w-10 text-white" /></div>
            <p className="font-display font-extrabold text-lg" style={{ color: "var(--brand-primary)" }}>{business.reward_title}</p>
            <p className="text-sm text-slate-500 leading-relaxed">Die Einlösung erfolgt durch einen Mitarbeiter an der Theke.</p>
          </div>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {celebrate && (
          <motion.div data-testid="celebration-banner" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setCelebrate(false)}>
            <motion.div initial={{ scale: 0.6, y: 40 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 220, damping: 18 }} className="bg-white rounded-3xl p-7 text-center max-w-xs w-full">
              <Sparkles className="h-10 w-10 mx-auto mb-2" style={{ color: "var(--brand-secondary)" }} />
              <h2 className="font-display font-extrabold text-2xl text-slate-900">Glückwunsch! 🎉</h2>
              <p className="text-slate-600 mt-1">Deine Belohnung ist bereit.</p>
              <div className="my-4 rounded-2xl p-3 border-2" style={{ background: "color-mix(in srgb, var(--brand-primary) 8%, white)", borderColor: "var(--brand-primary)" }}>
                <p className="font-display font-extrabold" style={{ color: "var(--brand-primary)" }}>{business.reward_title}</p>
              </div>
              <Button data-testid="celebration-show-reward-button" onClick={() => { setCelebrate(false); setRewardOpen(true); }} className="w-full h-12 rounded-2xl font-display font-bold text-white" style={{ background: "var(--brand-primary)" }}>
                Belohnung anzeigen
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
